import React from "react"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import colors from "@constants/colors"
import { body, h6 } from "@constants/typography"
import Link from "next/link"
import { StyledLink } from "@components/buttons/LinkButton"
import ImageHotspot, { ImageHotspotItem } from "@components/ImageHotspot"

interface HeroInfoBlockProps {
  subjectImageSrc: string
  subjectImageAlt?: string
  hotspots?: ImageHotspotItem[]
  layout?: "left" | "right"
  headingText?: string
  headingTextColor?: string
  headerImage?: string
  body?: string
  bodyTextColor?: string
  buttonText?: string
  buttonTextColor?: string
  buttonBgColor?: string
  buttonLink?: string
  bgImageSrc?: string
  bgColor?: string
}
const Section = styled.section<{ bgImage?: string; bgColor?: string }>`
  ${({ bgImage, bgColor }) => css`
    width: 100%;
    ${bgImage
      ? css`
          background-image: url(${bgImage});
          background-size: cover;
          background-position: center;
        `
      : css`
          background-color: #${bgColor || "000"};
        `}
  `}
`

const Container = styled.div<{ layout: "left" | "right" }>`
  ${({ layout }) => css`
    margin: 0 auto;
    max-width: 1600px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding: 44px 28px;

    ${up("laptop")} {
      flex-direction: ${layout === "right" ? "row-reverse" : "row"};
      align-items: center;
      gap: 40px;
      padding: 64px 40px;
    }
  `}
`

const SubjectImage = styled.img.attrs({
  decoding: "async",
})`
  width: 100%;
  height: auto;
  max-width: 640px;
  object-fit: contain;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.35));

  ${up("laptop")} {
    flex: 1 1 50%;
    margin: 0 auto;
  }
`

const SubjectHotspotImage = styled.div`
  width: 100%;
  max-width: 640px;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.35));

  ${up("laptop")} {
    flex: 1 1 50%;
    margin: 0 auto;
  }
`

const SubjectImagePlaceholder = styled.div`
  width: 100%;
  max-width: 640px;
  min-height: 320px;

  ${up("laptop")} {
    flex: 1 1 50%;
    margin: 0 auto;
    min-height: 420px;
  }
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 520px;

  ${up("laptop")} {
    flex: 1 1 50%;
    justify-content: center;
    margin: 0 auto;
  }
`

const HeaderImage = styled.img.attrs({
  decoding: "async",
})`
  width: auto;
  height: auto;
  max-width: 100%;
  object-fit: contain;
`

const HeaderImagePlaceholder = styled.div`
  width: 100%;
  min-height: 44px;
`

const Heading = styled.h6<{ color?: string }>`
  ${({ color }) => css`
    ${h6}
    margin: 0;
    color: ${color || colors.snow};
    font-size: 28px;
    line-height: 34px;

    ${up("laptop")} {
      font-size: 32px;
      line-height: 38px;
    }
  `}
`

const Body = styled.p<{ color?: string }>`
  ${({ color }) => css`
    ${body}
    margin: 0;
    color: ${color || "#fff"};
    opacity: 0.85;
    white-space: pre-line;
  `}
`

const ButtonRow = styled.div`
  margin-top: 10px;
  display: flex;
  align-self: flex-start;
`

const PillLink = styled(StyledLink)`
  ${css`
    text-transform: uppercase;
    letter-spacing: 1.5px;
    font-size: 12px;
    height: 36px;
    padding: 0 15px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.22);
    gap: 12px;
    transition: opacity 160ms ease;

    &:hover,
    &:focus-visible {
      opacity: 0.7;
    }
  `}
`

const Caret = styled.span`
  font-size: 21px;
`

const HeroInfoBlock: React.FC<HeroInfoBlockProps> = ({
  subjectImageSrc,
  subjectImageAlt,
  hotspots = [],
  headerImage,
  layout = "left",
  headingText,
  headingTextColor,
  body,
  bodyTextColor,
  buttonText,
  buttonTextColor,
  buttonBgColor,
  buttonLink,
  bgImageSrc,
  bgColor,
}) => {
  return (
    <Section bgImage={bgImageSrc} bgColor={bgColor}>
      <Container layout={layout}>
        {subjectImageSrc && hotspots.length > 0 ? (
          <SubjectHotspotImage>
            <ImageHotspot
              image={{
                src: subjectImageSrc,
                alt: subjectImageAlt,
              }}
              hotspots={hotspots}
            />
          </SubjectHotspotImage>
        ) : subjectImageSrc ? (
          <SubjectImage src={subjectImageSrc} alt={subjectImageAlt} />
        ) : (
          <SubjectImagePlaceholder />
        )}
        <TextBlock>
          {headerImage ? (
            <HeaderImage src={headerImage} alt="" />
          ) : (
            <HeaderImagePlaceholder />
          )}
          {headingText && (
            <Heading color={headingTextColor}>{headingText}</Heading>
          )}
          {body && <Body color={bodyTextColor}>{body}</Body>}
          {buttonText && buttonLink && (
            <ButtonRow>
              <Link href={buttonLink} passHref prefetch={false}>
                <PillLink
                  $buttonColor={buttonBgColor}
                  $buttonTextColor={buttonTextColor}
                >
                  {buttonText}
                  <Caret>&rsaquo;</Caret>
                </PillLink>
              </Link>
            </ButtonRow>
          )}
        </TextBlock>
      </Container>
    </Section>
  )
}

export default HeroInfoBlock
